/**
 * Demo / mock data generators for the Intelligence and Vehicle Stock
 * Intelligence tabs. Used as a fallback when the backend hasn't yet
 * shipped the real bundles so the UI can be reviewed with realistic
 * data.
 *
 * Outputs are deterministic per `part.id` so the values don't jitter
 * between renders. Once the real endpoints ship, the consuming
 * components prefer real data and ignore these.
 */

import {
  IPart,
  IPartIntelligence,
  IVehicleStockIntelligence,
} from "../../../models/Part";

/** Tiny deterministic 0..1 PRNG seeded from a string. */
function seededRand(seed: string) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const between = (rand: () => number, min: number, max: number) =>
  Math.round(min + rand() * (max - min));

const betweenF = (
  rand: () => number,
  min: number,
  max: number,
  decimals = 1
) => {
  const v = min + rand() * (max - min);
  const m = Math.pow(10, decimals);
  return Math.round(v * m) / m;
};

const pick = <T>(rand: () => number, items: T[]): T =>
  items[Math.floor(rand() * items.length)];

export function buildMockPartIntelligence(part: IPart): IPartIntelligence {
  const rand = seededRand(part.id || "demo");
  const currentPrice = part.price ?? between(rand, 60, 450);

  // Suggested price drifts ±25% around the current price.
  const drift = (rand() - 0.45) * 0.5; // -0.225..+0.275
  const suggested = Math.max(
    10,
    Math.round((currentPrice * (1 + drift)) / 5) * 5
  );
  const lowerBand = Math.round(suggested * 0.9);
  const upperBand = Math.round(suggested * 1.15);

  const overall = between(rand, 55, 92);
  const titleScore = Math.max(20, Math.min(100, overall + between(rand, -15, 10)));
  const photosScore = Math.max(20, Math.min(100, overall + between(rand, -25, 5)));
  const descScore = Math.max(20, Math.min(100, overall + between(rand, -20, 15)));
  const fitScore = Math.max(20, Math.min(100, overall + between(rand, -10, 20)));

  const issues: string[] = [];
  if (photosScore < 70) issues.push("Only 3 photos uploaded — recommend at least 6 (top, bottom, sides, label, MPN, defects).");
  if (titleScore < 75) issues.push("Title is missing the OEM number — buyers search by OEM far more than by description.");
  if (descScore < 75) issues.push("Description doesn't mention condition, mileage, or warranty terms.");
  if (fitScore < 80) issues.push("Fitment list is short — adding compatible variants increases search hits.");
  if (issues.length === 0) issues.push("Listing scores are healthy — no urgent issues detected.");

  const recommendations = [
    {
      id: "rec-1",
      title:
        suggested < currentPrice
          ? `Reduce price to ${"£"}${suggested}`
          : `Raise price to ${"£"}${suggested}`,
      description:
        suggested < currentPrice
          ? "Comparable parts are selling 8–12% below your current price — a small reduction should accelerate the sell-through without hurting margin much."
          : "Recent comparable sales support a higher asking price; current listing is leaving margin on the table.",
      impact: "high" as const,
      category: "Pricing",
    },
    {
      id: "rec-2",
      title: "Add 3 more photos",
      description:
        "Listings with 6+ photos convert ~38% better in this category. Prioritise the OEM/MPN sticker and any visible defects.",
      impact: photosScore < 70 ? ("high" as const) : ("medium" as const),
      category: "Photos",
    },
    {
      id: "rec-3",
      title: "Include OEM number in the title",
      description:
        "Most buyers search by OEM. Putting it in the first 80 characters of the title typically lifts impressions by 20–30%.",
      impact: "medium" as const,
      category: "Title",
    },
    {
      id: "rec-4",
      title: "Expand fitment list",
      description:
        "AutoDap suggests this part is also compatible with 4 additional variants we haven't listed.",
      impact: "low" as const,
      category: "Fitment",
    },
  ];

  const trend = pick(rand, ["Rising", "Stable", "Stable", "Declining"]);
  const sellProbability30d = between(rand, 25, 75);
  const sellProbability60d = Math.min(95, sellProbability30d + between(rand, 8, 18));
  const sellProbability90d = Math.min(99, sellProbability60d + between(rand, 5, 12));
  const estimatedDaysToSell = between(rand, 14, 110);

  const similarTitles = [
    "Genuine OEM unit — tested, 60-day warranty",
    "Used part in good working order, fast dispatch",
    "Refurbished unit — full warranty, ships next day",
    "Original equipment, low mileage, fully tested",
    "Pre-owned, fully functional, free UK shipping",
    "OEM replacement, excellent condition",
  ];
  const conditions = ["Used", "Used – Excellent", "Refurbished", "Used – Good"];
  const sources = ["eBay", "eBay"];
  const similarListings = Array.from({ length: 6 }).map((_, i) => {
    const r = seededRand(`${part.id}-similar-${i}`);
    const sold = r() > 0.5;
    return {
      id: `mock-similar-${i}`,
      title: pick(r, similarTitles),
      price: Math.round((currentPrice + (r() - 0.5) * currentPrice * 0.6) / 5) * 5,
      url: "https://www.ebay.co.uk/sch/i.html?_sacat=6028&_from=R40&LH_Sold=1&LH_Complete=1",
      thumbnailUrl: null,
      source: pick(r, sources),
      condition: pick(r, conditions),
      soldDate: sold ? new Date(Date.now() - r() * 1000 * 60 * 60 * 24 * 60).toISOString() : null,
    };
  });

  return {
    generatedAt: new Date(Date.now() - between(rand, 5, 240) * 60 * 1000).toISOString(),
    model: "wilma-pricing-demo-v1",
    pricing: {
      suggestedPrice: suggested,
      confidence: between(rand, 62, 92),
      reasoning:
        suggested < currentPrice
          ? `Based on ${between(rand, 18, 42)} comparable sales in the last 90 days, parts of this type are clearing at a slightly lower price point. Lowering the asking price into the £${lowerBand}–£${upperBand} band should keep margin healthy while shortening days-on-site.`
          : `Recent comparable sales (${between(rand, 12, 28)} in the last 90 days) cleared above your current asking price. The £${lowerBand}–£${upperBand} band reflects what the market is currently paying for parts of this grade and condition.`,
      lowerBand,
      upperBand,
    },
    listingQuality: {
      overall,
      title: titleScore,
      photos: photosScore,
      description: descScore,
      fitment: fitScore,
      issues,
    },
    demand: {
      sellProbability30d,
      sellProbability60d,
      sellProbability90d,
      estimatedDaysToSell,
      trend,
    },
    recommendations,
    similarListings,
  };
}

export function buildMockVehicleStockIntelligence(
  part: IPart
): IVehicleStockIntelligence {
  const rand = seededRand(`${part.id || "demo"}-vehicle`);
  const v = part.vehicle;
  const year =
    v?.modelYear ?? v?.dvlaYearOfManufacture ?? between(rand, 2010, 2022);
  const make = v?.combinedMake ?? v?.dvlaMake ?? "BMW";
  const model = v?.combinedModel ?? v?.dvlaModel ?? "3 Series";
  const mark = v?.mark ?? null;

  const partsInStockForYmm = between(rand, 60, 220);
  const sameCategoryInStock = between(rand, 0, 7);
  const salesLast30 = between(rand, 6, 24);
  const salesLast60 = salesLast30 + between(rand, 8, 22);
  const salesLast90 = salesLast60 + between(rand, 6, 18);
  const averagePartsSoldPerVehicle = betweenF(rand, 8, 22, 1);
  const averageRevenuePerVehicle = between(rand, 1100, 3800);
  const averageVehiclePurchasePrice = between(rand, 600, 2400);
  // Simple model: days = (purchase / (revenue / 90))
  const estimatedDaysToBreakEven = Math.max(
    14,
    Math.round(
      averageVehiclePurchasePrice /
        Math.max(1, averageRevenuePerVehicle / 90)
    )
  );

  return {
    generatedAt: new Date(Date.now() - between(rand, 10, 360) * 60 * 1000).toISOString(),
    vehicle: { year, make, model, mark },
    partsInStockForYmm,
    sameCategoryInStock,
    salesLast30,
    salesLast60,
    salesLast90,
    averagePartsSoldPerVehicle,
    averageRevenuePerVehicle,
    averageVehiclePurchasePrice,
    estimatedDaysToBreakEven,
  };
}
